<script>
import axios from "axios";
import IconOTA from "@/components/Reservas/IconOTABackground.vue";
import { formatCurrencyCode } from "@/utils/formatters";

export default {
  props: {
    reservationData: {
      type: Object,
      required: true,
    },
    formatDate: {
      type: Function,
      required: true,
    },
    formatDates: {
      type: Function,
      required: true,
    },
    getNights: {
      type: Function,
      required: true,
    },
    handleConfirmationCancellation: {
      type: Function,
      required: true,
    },
    cancellingBooking: {
      type: Boolean,
    },
  },
  components: {
    IconOTA,
  },

  data() {
    return {
      // Copia local del early check-in para poder actualizarla tras una acción.
      earlyState: this.reservationData ? this.reservationData.early_checkin || null : null,
      earlyBusy: false,
      earlyModal: { visible: false, action: null, time: "" },
    };
  },

  watch: {
    "reservationData.early_checkin": {
      immediate: true,
      handler(val) {
        this.earlyState = val || null;
      },
    },
    /*
     * La ventana de confirmación se saca a <body> al abrirse.
     *
     * Esta ficha vive dentro del drawer de la reserva, del chat, de la factura…
     * y cualquier contenedor con transform, filtro o animación convierte un
     * `position: fixed` en relativo a él: la ventana se queda encerrada, cortada
     * o por debajo del drawer. Desde <body> no hay nada que la atrape. Vue la
     * sigue quitando él solo al cerrarla (usa su padre real, sea cual sea).
     */
    "earlyModal.visible"(abierta) {
      if (!abierta) return;
      this.$nextTick(() => {
        const el = this.$refs.earlyModal;
        if (!el) return;
        if (el.parentNode !== document.body) document.body.appendChild(el);
        /*
         * Y por encima de lo que haya abierto, calculado en el momento.
         *
         * El drawer de la reserva NO tiene una capa fija: PrimeVue le suma su
         * contador global cada vez que se abre (11050 + contador) y ese
         * contador sube con cada desplegable o diálogo. Con un número fijo
         * (13000) la ventana quedó DEBAJO de un drawer en 23107 en el banco de
         * pruebas: se veía, pero el botón de confirmar no recibía el clic.
         */
        const capas = [...document.querySelectorAll(".p-sidebar, .p-sidebar-mask, .p-dialog-mask, .modal, .modal-backdrop")]
          .map((n) => parseInt(window.getComputedStyle(n).zIndex, 10) || 0);
        el.style.zIndex = String(Math.max(13000, ...capas) + 10);
      });
    },
  },

  beforeDestroy() {
    // Si la ficha se cierra con la ventana abierta, que no se quede huérfana en <body>.
    const el = this.$refs.earlyModal;
    if (el && el.parentNode === document.body) document.body.removeChild(el);
  },

  computed: {
    // Early check-in solicitado (acceso temprano), o null.
    earlyCheckin() {
      return this.earlyState || null;
    },
    // ¿La solicitud está pendiente de decisión del anfitrión?
    earlyPending() {
      return !!this.earlyState && this.earlyState.status === "requested";
    },
    // Cobro enviado y sin pagar: no hay nada que aprobar, solo que esperar.
    earlyAwaitingPayment() {
      return !!this.earlyState && !!this.earlyState.awaiting_payment;
    },
    earlyCheckinNote() {
      const e = this.earlyCheckin;
      if (!e) return "";
      if (e.status === "confirmed") return this.$t("reservasSmallHc.reservationData.statusConfirmed");
      if (e.status === "rejected") return this.$t("reservasSmallHc.reservationData.statusRejected");
      if (e.tentative) return this.$t("reservasSmallHc.reservationData.statusTentative");
      if (!e.is_early) return this.$t("reservasSmallHc.reservationData.statusWithinCheckin");
      if (e.mode === "free" || !e.price) return this.$t("reservasSmallHc.reservationData.statusFree");
      return `${e.price} ${e.currency || ""}`.trim();
    },
    /*
     * En qué punto está la entrada anticipada, tal como la tiene que leer el
     * anfitrión: pendiente (con botones), priorizada, aprobada o rechazada.
     * Una hora de llegada normal (no anticipada) es su propio caso.
     */
    earlyDecision() {
      const e = this.earlyCheckin;
      if (!e) return null;
      if (e.status === "confirmed") return "approved";
      if (e.status === "rejected") return "rejected";
      if (e.prioritised) return "prioritised";
      return "pending";
    },
    // Hora de llegada NORMAL (a la hora del check-in o después): no es una entrada
    // anticipada y no se le puede llamar así en la caja, la ventana ni el aviso.
    earlyIsArrival() {
      return !!this.earlyCheckin && !this.earlyCheckin.is_early;
    },
    earlyBoxClass() {
      return {
        "rsv-early--plain": this.earlyIsArrival && this.earlyDecision === "pending",
        "rsv-early--ok": this.earlyDecision === "approved",
        "rsv-early--no": this.earlyDecision === "rejected",
      };
    },
    earlyTitle() {
      const k = "reservasSmallHc.reservationData.";
      if (this.earlyIsArrival) {
        return {
          approved: this.$t(k + "arrivalApprovedTitle"),
          rejected: this.$t(k + "arrivalRejectedTitle"),
        }[this.earlyDecision] || this.$t(k + "arrivalRequested");
      }
      return {
        approved: this.$t(k + "earlyApprovedTitle"),
        rejected: this.$t(k + "earlyRejectedTitle"),
        prioritised: this.$t(k + "earlyPrioritisedTitle"),
        pending: this.$t(k + "earlyRequested"),
      }[this.earlyDecision] || "";
    },
    earlyLine() {
      const e = this.earlyCheckin;
      if (!e) return "";
      const k = "reservasSmallHc.reservationData.";
      const precio = e.mode === "paid" && e.price ? ` · ${e.price} ${e.currency || ""}`.trimEnd() : "";
      switch (this.earlyDecision) {
        case "approved":
          return (e.original_time && e.original_time !== e.time
            ? this.$t(k + "earlyLineApprovedOther", { time: e.time, original: e.original_time })
            : this.$t(k + "earlyLineApproved", { time: e.time })) + precio;
        case "rejected":
          if (this.earlyIsArrival) return this.$t(k + "arrivalLineRejected", { time: e.time });
          return e.standard_checkin
            ? this.$t(k + "earlyLineRejectedStd", { time: e.time, std: e.standard_checkin })
            : this.$t(k + "earlyLineRejected", { time: e.time });
        case "prioritised":
          return this.$t(k + "earlyLinePrioritised", { time: e.time });
        default: {
          const pidio = e.original_time && e.original_time !== e.time
            ? ` · ${this.$t(k + "requestedTimeHint", { time: e.original_time })}`
            : "";
          return this.$t(k + "arrivesAt", { time: e.time }) + (this.earlyCheckinNote ? ` · ${this.earlyCheckinNote}` : "") + pidio;
        }
      }
    },
    /*
     * Salida tardía: su propia caja, debajo de la de la entrada.
     *
     * No se veía en ningún sitio: el huésped la pedía y la pagaba, y el anfitrión
     * solo se enteraba leyendo el chat. El servidor la manda ya resuelta
     * (`late_checkout`): pendiente de pago, confirmada (pagada, de cortesía o en
     * efectivo) o rechazada. Aquí no hay botones: no hay nada que decidir.
     */
    lateCheckout() {
      return (this.reservationData && this.reservationData.late_checkout) || null;
    },
    /* La ampliación de la estancia: su propia caja, como la entrada y la salida.
       Una reserva puede tener las tres peticiones y se enseñan juntas. */
    stayExtension() {
      return (this.reservationData && this.reservationData.stay_extension) || null;
    },
    extDecision() {
      const e = this.stayExtension;
      if (!e) return null;
      if (e.status === "confirmed") return "confirmed";
      if (e.awaiting_payment) return "awaiting";
      return "pending";
    },
    extTitle() {
      const k = "reservasSmallHc.reservationData.";
      return {
        confirmed: this.$t(k + "extConfirmedTitle"),
        awaiting: this.$t(k + "extAwaitingTitle"),
        pending: this.$t(k + "extRequestedTitle"),
      }[this.extDecision] || "";
    },
    extLine() {
      const e = this.stayExtension;
      if (!e) return "";
      const k = "reservasSmallHc.reservationData.";
      const fecha = e.new_checkout ? this.formatDate(e.new_checkout) : "";
      const importe = `${e.price} ${e.currency || ""}`.trim();
      const que = this.$t(k + "extNights", { nights: e.nights, date: fecha });

      let pago = "";
      if (!e.price) pago = this.$t(k + "lateFree");
      else if (e.paid) pago = this.$t(k + "latePaid", { amount: importe });
      else if (e.cash) pago = this.$t(k + "extAtProperty", { amount: importe });
      else if (e.pending_host) pago = `${importe} · ${this.$t(k + "extPendingHost")}`;
      else pago = importe;

      return `${que} · ${pago}`;
    },
    lateDecision() {
      const l = this.lateCheckout;
      if (!l) return null;
      if (l.status === "rejected") return "rejected";
      if (l.status === "confirmed") return "confirmed";
      return l.awaiting_payment ? "awaiting" : "pending";
    },
    lateBoxClass() {
      return {
        "rsv-late--ok": this.lateDecision === "confirmed",
        "rsv-late--no": this.lateDecision === "rejected",
      };
    },
    lateTitle() {
      const k = "reservasSmallHc.reservationData.";
      return {
        confirmed: this.$t(k + "lateConfirmedTitle"),
        rejected: this.$t(k + "lateRejectedTitle"),
        awaiting: this.$t(k + "lateAwaitingTitle"),
        pending: this.$t(k + "lateRequestedTitle"),
      }[this.lateDecision] || "";
    },
    lateLine() {
      const l = this.lateCheckout;
      if (!l) return "";
      const k = "reservasSmallHc.reservationData.";
      const std = l.standard_checkout && l.standard_checkout !== l.time ? l.standard_checkout : null;
      const importe = `${l.price} ${l.currency || ""}`.trim();

      if (this.lateDecision === "rejected") {
        return std
          ? this.$t(k + "lateLineRejectedStd", { time: l.time, std })
          : this.$t(k + "lateLineRejected", { time: l.time });
      }

      const cuando = (this.lateDecision === "confirmed"
        ? this.$t(k + "lateLeavesAt", { time: l.time })
        : this.$t(k + "lateWantsToLeave", { time: l.time }))
        + (std ? ` ${this.$t(k + "lateInsteadOf", { std })}` : "");

      let pago = "";
      if (!l.price) {
        pago = l.kind === "courtesy" ? this.$t(k + "lateCourtesy") : this.$t(k + "lateFree");
      } else if (l.paid) {
        pago = this.$t(k + "latePaid", { amount: importe });
      } else if (l.cash) {
        pago = this.$t(k + "lateCash", { amount: importe });
      } else if (this.lateDecision === "confirmed") {
        pago = this.$t(k + "lateUnpaid", { amount: importe });
      } else {
        pago = importe;
      }

      return `${cuando} · ${pago}`;
    },
    earlyModalTitle() {
      return {
        approve: this.$t("reservasSmallHc.reservationData.modalTitleApprove"),
        "approve-time": this.$t("reservasSmallHc.reservationData.modalTitleApproveTime"),
        tentative: this.$t("reservasSmallHc.reservationData.modalTitlePrioritize"),
        reject: this.$t("reservasSmallHc.reservationData.modalTitleReject"),
      }[this.earlyModal.action] || this.$t("reservasSmallHc.reservationData.modalTitleDefault");
    },
    earlyModalText() {
      if (this.earlyIsArrival && ["approve", "reject"].includes(this.earlyModal.action)) {
        return this.$t(this.earlyModal.action === "approve"
          ? "reservasSmallHc.reservationData.modalTextApproveArrival"
          : "reservasSmallHc.reservationData.modalTextRejectArrival");
      }
      return {
        approve: this.$t("reservasSmallHc.reservationData.modalTextApprove"),
        "approve-time": this.$t("reservasSmallHc.reservationData.modalTextApproveTime"),
        tentative: this.$t("reservasSmallHc.reservationData.modalTextPrioritize"),
        reject: this.$t("reservasSmallHc.reservationData.modalTextReject"),
      }[this.earlyModal.action] || this.$t("reservasSmallHc.reservationData.modalTextDefault");
    },
    earlyModalCta() {
      return {
        approve: this.$t("reservasSmallHc.reservationData.modalCtaApprove"),
        "approve-time": this.$t("reservasSmallHc.reservationData.modalCtaApproveTime"),
        tentative: this.$t("reservasSmallHc.reservationData.modalCtaPrioritize"),
        reject: this.$t("reservasSmallHc.reservationData.modalCtaReject"),
      }[this.earlyModal.action] || this.$t("reservasSmallHc.reservationData.modalCtaDefault");
    },
    isPastReservation() {
      const raw =
        this.reservationData?.fecha_salida ||
        this.reservationData?.fecha_ingreso;
      if (!raw) return false;

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const target = new Date(raw);
      target.setHours(0, 0, 0, 0);

      return target < today;
    },

    canEdit() {
      // Se permite editar también reservas pasadas: muchos clientes cargan
      // reservas antiguas y necesitan poder corregir errores (fechas, nombre, etc.)
      return true;
    },

    canChangeStatus() {
      return (
        !this.isPastReservation && this.reservationData?.status === "booked"
      );
    },

    // ✅ Lista segura
    roomInstances() {
      return Array.isArray(this.reservationData?.room_instances)
        ? this.reservationData.room_instances
        : [];
    },

    hasAssignedRooms() {
      return this.roomInstances.length > 0;
    },

    roomsCount() {
      return this.roomInstances.length;
    },

    // ✅ Para el caso de 1 sola (texto directo)
    singleRoomLabel() {
      if (!this.hasAssignedRooms) return "";
      return this.formatRoom(this.roomInstances[0]);
    },

    // ¿La propiedad es de tipo hotel/camping (con habitaciones) o un alojamiento entero?
    isHotelProperty() {
      const type = String(this.reservationData?.propiedad?.type || "").toLowerCase();
      if (type === "hotel" || type === "camping") return true;
      // Si tiene habitaciones asignadas, también lo tratamos como hotelero
      return this.hasAssignedRooms;
    },

    // Etiqueta del primer KPI: "Habitación" (hotel) o "Alojamiento" (apartamento)
    stayKpiLabel() {
      return this.isHotelProperty ? this.$t("reservasSmallHc.reservationData.kpiRoom") : this.$t("reservasSmallHc.reservationData.kpiLodging");
    },

    // Reserva COMPUESTA (grupo): habitaciones/huéspedes de TODA la reserva.
    isGroup() {
      return Boolean(this.reservationData?.is_group);
    },
    groupRooms() {
      return Number(this.reservationData?.group_rooms || 0);
    },
    groupGuests() {
      return Number(this.reservationData?.group_guests || 0);
    },

    // Desglose de huéspedes: "3 adultos · 2 niños · 1 bebé". Si la reserva no trae
    // el desglose (OTA/históricas), cae al total simple (cantidad_huesped).
    guestsBreakdownLabel() {
      const total = this.isGroup ? this.groupGuests : Number(this.reservationData?.cantidad_huesped || 0);
      const a = Number(this.reservationData?.adults || 0);
      const c = Number(this.reservationData?.children || 0);
      const i = Number(this.reservationData?.infants || 0);
      // Sin desglose persistido → total simple
      if (a + c + i === 0) {
        return `${total} ${this.$tc("reservasSmallHc.reservationData.guestsCount", total)}`;
      }
      const parts = [];
      if (a > 0) parts.push(`${a} ${this.$tc("reservasSmallHc.reservationData.adultsCount", a)}`);
      if (c > 0) parts.push(`${c} ${this.$tc("reservasSmallHc.reservationData.childrenCount", c)}`);
      if (i > 0) parts.push(`${i} ${this.$tc("reservasSmallHc.reservationData.infantsCount", i)}`);
      return parts.join(" · ");
    },

    // Valor del primer KPI
    stayKpiValue() {
      // En reserva compuesta muestra el total de habitaciones del grupo.
      if (this.isGroup) return this.$t("reservasSmallHc.reservationData.roomsCountValue", { count: this.groupRooms });
      if (this.isHotelProperty) {
        if (!this.hasAssignedRooms) return "—";
        if (this.roomsCount === 1) return this.singleRoomLabel;
        return this.$t("reservasSmallHc.reservationData.roomsCountValue", { count: this.roomsCount });
      }
      // Apartamento: el nombre del alojamiento
      return this.reservationData?.propiedad?.nombre || "—";
    },

    // Subtítulo del primer KPI
    staySubLabel() {
      if (this.isHotelProperty) {
        return this.reservationData?.propiedad?.nombre || "";
      }
      // Apartamento: solo el nombre interno si existe (sin texto genérico)
      return this.reservationData?.propiedad?.internalName || "";
    },

    hasEmail() {
      const e = String(this.reservationData?.email || "").trim().toLowerCase();
      return !!e && e !== "no-email@example.com" && e !== "no email provided";
    },

    hasPhone() {
      const p = String(this.reservationData?.telefono || "").replace(/\D+/g, "");
      return p.length >= 5;
    },

    // Bandera del país según el código del teléfono (igual que el buzón de Mensajes)
    phoneFlagUrl() {
      const code = this.resolvePhoneCountryCode(this.reservationData?.telefono);
      return code ? `/Flags/${code}.png` : null;
    },

    // Iniciales para el avatar
    guestInitials() {
      const name = String(this.reservationData?.nombre || "").trim();
      if (!name) return "?";
      const parts = name.split(/\s+/).filter(Boolean);
      const first = parts[0]?.[0] || "";
      const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
      return (first + last).toUpperCase();
    },

    nightsCount() {
      return this.getNights(
        this.reservationData?.fecha_ingreso,
        this.reservationData?.fecha_salida
      );
    },

    // Total autoritativo (incluye cargos vía total_to_pay) con el código de moneda
    totalFormatted() {
      const total =
        this.reservationData?.total_to_pay ??
        this.reservationData?.price ??
        0;
      return formatCurrencyCode(total, this.reservationData?.propiedad?.currency);
    },
  },

  methods: {
    // ── Acciones del anfitrión sobre la solicitud de early check-in ──
    earlyAsk(action) {
      if (this.earlyBusy) return;
      this.earlyModal = {
        visible: true,
        action,
        time: action === "approve-time" ? (this.earlyState && this.earlyState.time) || "" : "",
      };
    },
    earlyCloseModal() {
      this.earlyModal = { visible: false, action: null, time: "" };
    },
    async earlyProceed() {
      const { action, time } = this.earlyModal;
      if (!action) return;
      if (action === "approve-time" && !time) return;
      this.earlyCloseModal();
      await this.earlyAct(action, action === "approve-time" ? { time } : {});
    },
    /*
     * Manda la decisión y deja la caja como quedó.
     *
     * Antes la caja se rehacía a mano con lo que venía en `data`, que no traía
     * `prioritised`: tras «Priorizar» los botones seguían ahí y parecía que el
     * clic no había hecho nada (el mensaje al huésped sí había salido). Ahora el
     * servidor manda la caja ya calculada (`early_checkin`), la misma que pinta
     * al abrir la reserva. Y si algo falla, se dice: nada de silencios.
     */
    async earlyAct(action, payload = {}) {
      const e = this.earlyState;
      if (!e || !e.id || this.earlyBusy) return;
      this.earlyBusy = true;
      try {
        const { data } = await axios.post(`/agent-ia/requests/${e.id}/${action}`, payload);
        this.earlyApplyServerState(data, action);
        // Un servidor anterior no manda `notified`: entonces solo se dice que se guardó.
        const sabeSiSalio = !!data && Object.prototype.hasOwnProperty.call(data, "notified");
        this.earlyToastResult(action, sabeSiSalio ? data.notified : true);
        // Se cobra online y Stripe no dio enlace: que el anfitrión lo cobre de otra forma.
        if (data && data.payment_link_failed) this.earlyToast("warn", "toastPaymentLinkFailed");
        // Ya estaba pagada: no se le cobra otra vez; y si ya no entra antes, quizá haya que devolver.
        if (data && data.refund_maybe_due) this.earlyToast("warn", "toastRefundMaybeDue");
        else if (data && data.already_paid) this.earlyToast("info", "toastAlreadyPaid");
        this.$emit("early-decided", action);
      } catch (err) {
        const res = err && err.response;
        // Ya estaba decidida (otra pantalla, otra persona): se enseña cómo quedó.
        if (res && res.status === 409 && res.data) {
          this.earlyApplyServerState(res.data, action);
          this.earlyToast("info", "toastAlreadyDecided");
          this.$emit("early-decided", action);
        } else {
          this.earlyToast("error", "toastError");
        }
      } finally {
        this.earlyBusy = false;
      }
    },
    earlyApplyServerState(data, action) {
      if (data && data.early_checkin) {
        this.earlyState = data.early_checkin;
        return;
      }
      // Servidor sin `early_checkin` (versión anterior): lo mínimo para que la
      // caja no se quede con los botones de una decisión ya tomada.
      const r = data && data.data;
      if (!r) return;
      this.earlyState = {
        ...this.earlyState,
        status: r.status,
        time: r.requested_time || this.earlyState.time,
        original_time: r.original_time || this.earlyState.original_time || null,
        tentative: !!r.tentative,
        prioritised: !!r.prioritised || action === "tentative",
        awaiting_payment: !!r.awaiting_payment,
      };
    },
    earlyToastResult(action, notified) {
      const clave = {
        approve: this.earlyIsArrival ? "toastArrivalApproved" : "toastApproved",
        "approve-time": "toastApprovedTime",
        tentative: "toastPrioritised",
        reject: this.earlyIsArrival ? "toastArrivalRejected" : "toastRejected",
      }[action];
      if (!clave) return;
      if (notified === false) {
        // Guardado, pero el mensaje no salió: que el anfitrión lo sepa y le escriba.
        this.earlyToast("warn", clave, "toastNotNotified");
      } else if (notified === null || notified === undefined) {
        // Decisión repetida: no se le ha vuelto a escribir al huésped.
        this.earlyToast("info", "toastAlreadyDecided");
      } else {
        this.earlyToast("success", clave, "toastNotified");
      }
    },
    earlyToast(severity, clave, detalle = null) {
      const k = "reservasSmallHc.reservationData.";
      if (!this.$toast || typeof this.$toast.add !== "function") return;
      this.$toast.add({
        severity,
        summary: this.$t(k + clave),
        detail: detalle ? this.$t(k + detalle) : undefined,
        life: severity === "error" || severity === "warn" ? 7000 : 4000,
      });
    },

    handleEditClick() {
      this.$emit("edit-reservation", this.reservationData);
    },

    // ✅ AQUÍ DEBE IR (methods) porque recibe parámetro
    formatRoom(room) {
      const code = String(room?.codigo || "").trim();
      const name = String(room?.nombre || "").trim();
      if (code && name) return `${code} - ${name}`;
      return name || code || "";
    },

    normalizePhoneDigits(phone) {
      return String(phone || "").replace(/\D+/g, "");
    },
    resolvePhoneCountryCode(phone) {
      const digits = this.normalizePhoneDigits(phone);
      if (!digits) return null;
      const map = [
        ["351", "PT"], ["593", "EC"], ["598", "UY"], ["591", "BO"],
        ["595", "PY"], ["507", "PA"], ["506", "CR"], ["503", "SV"],
        ["502", "GT"], ["505", "NI"], ["504", "HN"], ["509", "HT"],
        ["1809", "DO"], ["1829", "DO"], ["1849", "DO"],
        ["57", "CO"], ["55", "BR"], ["54", "AR"], ["56", "CL"],
        ["51", "PE"], ["52", "MX"], ["58", "VE"], ["34", "ES"],
        ["33", "FR"], ["39", "IT"], ["49", "DE"], ["44", "GBR"],
        ["31", "NL"], ["32", "BE"], ["41", "CH"], ["1", "US"],
      ];
      const match = map.find(([prefix]) => digits.startsWith(prefix));
      return match ? match[1] : null;
    },
  },
};
</script>

<template>
  <div class="rsv-hd" style="width: 100%">
    <small class="rsv-hd__eyebrow">
      {{ $t("booking.detailsTitle") }}
    </small>

    <!-- Cabecera: identidad (izq) + estado (der) -->
    <div class="rsv-hd__top">
      <div class="rsv-hd__id">
        <div class="rsv-hd__id-text">
          <span class="rsv-hd__role">{{ $t("reservasSmallHc.reservationData.holderRole") }}</span>
          <h1 class="rsv-hd__name">{{ reservationData.nombre }}</h1>

          <div class="rsv-hd__meta">
            <IconOTA
              :channel_id="Number(reservationData.id_channel)"
              :styleIcon="20"
              class="rsv-hd__ota"
            />
            <span class="rsv-hd__code">
              #{{ reservationData.ota_code || reservationData.codigo_reserva }}
            </span>
            <span class="rsv-hd__dot"></span>
            <span class="rsv-hd__muted">
              {{ $t("booking.createdOn") }}
              {{ formatDate(reservationData.created_at) }}
            </span>
          </div>

          <!-- Contacto del responsable (compacto, junto al nombre) -->
          <div class="rsv-hd__contact">
            <a
              v-if="hasEmail"
              :href="'mailto:' + reservationData.email"
              class="rsv-hd__cti"
              :title="reservationData.email"
            >
              <span class="rsv-hd__cti-text">{{ reservationData.email }}</span>
            </a>
            <!-- Sin dato se dejaba el hueco en blanco y no quedaba claro si faltaba
                 o no se había cargado. Las reservas de OTA llegan a menudo sin
                 contacto, así que se dice explícitamente. -->
            <span v-else class="rsv-hd__cti rsv-hd__cti--empty">
              <span class="rsv-hd__cti-text">{{ $t("reservasSmallHc.reservationData.noEmail") }}</span>
            </span>

            <a
              v-if="hasPhone"
              :href="'https://wa.me/' + normalizePhoneDigits(reservationData.telefono)"
              target="_blank"
              rel="noopener"
              class="rsv-hd__cti"
              :title="$t('reservasSmallHc.reservationData.writeWhatsapp')"
            >
              <img
                v-if="phoneFlagUrl"
                :src="phoneFlagUrl"
                class="rsv-hd__flag"
                alt="flag"
                @error="$event.target.style.display = 'none'"
              />
              <span class="rsv-hd__cti-text">{{ reservationData.telefono }}</span>
            </a>
            <span
              v-else
              class="rsv-hd__cti rsv-hd__cti--empty"
              :title="$t('reservasSmallHc.reservationData.noPhoneTitle')"
            >
              <span class="rsv-hd__cti-text">{{ $t("reservasSmallHc.reservationData.noPhone") }}</span>
            </span>
          </div>
        </div>
      </div>

      <div class="rsv-hd__status">
        <b-dropdown
          id="dropdown-1"
          v-if="reservationData.status == 'booked'"
          :disabled="!canChangeStatus || cancellingBooking"
          class="rsv-status-dd"
        >
          <template #button-content>
            <template v-if="!cancellingBooking">
              <span class="rsv-status-dot"></span>
              {{ $t("booking.statusBooked") }}
              <svg
                style="width: 16px; margin-left: 10px"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                class="size-6"
              >
                <path
                  fill-rule="evenodd"
                  d="M11.47 4.72a.75.75 0 0 1 1.06 0l3.75 3.75a.75.75 0 0 1-1.06 1.06L12 6.31 8.78 9.53a.75.75 0 0 1-1.06-1.06l3.75-3.75Zm-3.75 9.75a.75.75 0 0 1 1.06 0L12 17.69l3.22-3.22a.75.75 0 1 1 1.06 1.06l-3.75 3.75a.75.75 0 0 1-1.06 0l-3.75-3.75a.75.75 0 0 1 0-1.06Z"
                  clip-rule="evenodd"
                />
              </svg>
            </template>

            <template v-else>
              <span
                class="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              ></span>
              {{ $t("booking.processing") }}
            </template>
          </template>

          <b-dropdown-item
            @click="handleConfirmationCancellation()"
            :disabled="cancellingBooking || !canChangeStatus"
          >
            <i class="pi pi-times-circle mr-2"></i>
            {{ $t("booking.reject") }}
          </b-dropdown-item>
        </b-dropdown>

        <div
          v-else-if="reservationData.status == 'cancelled'"
          class="rsv-status-pill rsv-status-pill--cancelled"
        >
          <i class="pi pi-times-circle"></i>
          {{ $t("booking.cancelled") }}
        </div>

        <div v-else class="rsv-status-pill rsv-status-pill--ok">
          <span class="rsv-status-dot"></span>
          {{ reservationData.status }}
        </div>
      </div>
    </div>

    <!-- Tira de KPIs: Alojamiento/Habitación · Entrada · Salida (el Total va en "Importe y pago") -->
    <div class="rsv-hd__kpis">
      <div class="rsv-kpi rsv-kpi--green">
        <span class="rsv-kpi__label">{{ stayKpiLabel }}</span>
        <span class="rsv-kpi__value">{{ stayKpiValue }}</span>
        <span class="rsv-kpi__sub">{{ staySubLabel }}</span>
        <!-- Tarifa con la que se compró (en grupo la muestra el panel de habitaciones). -->
        <span v-if="!isGroup && reservationData.rate_plan_name" class="rsv-rate-chip">
          {{ reservationData.rate_plan_name }}
        </span>
      </div>

      <div class="rsv-kpi rsv-kpi--blue">
        <span class="rsv-kpi__label">{{ $t("reservasSmallHc.reservationData.kpiCheckin") }}</span>
        <span class="rsv-kpi__value">
          {{ formatDate(reservationData.fecha_ingreso) }}
        </span>
        <span class="rsv-kpi__sub" :title="guestsBreakdownLabel">
          {{ guestsBreakdownLabel }}
        </span>
      </div>

      <div class="rsv-kpi rsv-kpi--purple">
        <span class="rsv-kpi__label">{{ $t("reservasSmallHc.reservationData.kpiCheckout") }}</span>
        <span class="rsv-kpi__value">
          {{ formatDate(reservationData.fecha_salida) }}
        </span>
        <span class="rsv-kpi__sub">
          {{ nightsCount }} {{ $t("booking.nights") }}
        </span>
      </div>
    </div>

    <!--
      Hora de llegada / Early check-in — etiqueta bien visible.

      Después de decidir, la caja dice CÓMO quedó (aprobada a qué hora, priorizada,
      rechazada) en vez de seguir enseñando «solicitado» con los botones: así se ve
      que el clic hizo algo y qué se le dijo al huésped.
    -->
    <div v-if="earlyCheckin" class="rsv-early" :class="earlyBoxClass">
      <span class="rsv-early__ic">
        <svg v-if="earlyDecision === 'approved'" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        <svg v-else-if="earlyDecision === 'rejected'" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15 14"/></svg>
      </span>
      <div class="rsv-early__txt">
        <strong>{{ earlyTitle }}</strong>
        <span>{{ earlyLine }}</span>
      </div>

      <!--
        Los botones salen SOLO cuando de verdad queda algo que decidir.

        Si al huesped ya se le ha mandado el cobro, la entrada esta concedida y
        lo unico que falta es que pague: ahi «Aprobar» no significa nada —al
        pagar le llega el mismo mensaje que le llegaria al aprobar— y «Rechazar»
        obligaria a devolver el dinero. Se dice lo que pasa y ya.

        «Priorizar» si es una decision tomada: se mira `prioritised`, la marca de
        que la puso el anfitrion, y no `tentative`, que ya viene puesta desde la
        configuracion del anuncio y escondia los botones de solicitudes que nadie
        habia mirado.
      -->
      <div v-if="earlyAwaitingPayment" class="rsv-early__wait">
        {{ $t("reservasSmallHc.reservationData.awaitingPayment") }}
      </div>
      <div v-else-if="earlyPending && !earlyCheckin.prioritised" class="rsv-early__actions">
        <button type="button" class="rsv-eb rsv-eb--ok" :disabled="earlyBusy" @click="earlyAsk('approve')">{{ $t("reservasSmallHc.reservationData.actionApprove") }}</button>
        <button type="button" class="rsv-eb rsv-eb--alt" :disabled="earlyBusy" @click="earlyAsk('approve-time')">{{ $t("reservasSmallHc.reservationData.actionOtherTime") }}</button>
        <!-- «Priorizar» = «haremos lo posible por tenerlo listo ANTES»: con una hora de llegada normal no aplica. -->
        <button v-if="earlyCheckin.is_early" type="button" class="rsv-eb rsv-eb--alt" :disabled="earlyBusy" @click="earlyAsk('tentative')">{{ $t("reservasSmallHc.reservationData.actionPrioritize") }}</button>
        <button type="button" class="rsv-eb rsv-eb--no" :disabled="earlyBusy" @click="earlyAsk('reject')">{{ $t("reservasSmallHc.reservationData.actionReject") }}</button>
      </div>
      <!--
        Priorizada: al huésped ya se le dijo «haremos lo posible» y no se le vuelve
        a escribir solo. Queda CERRARLA cuando se sepa: si la habitación está lista,
        «Ya puede entrar» (le llega la confirmación y, si es de pago, el cobro); si
        no, «No va a ser posible». Sin esto, una de pago se quedaba sin cobrar.
      -->
      <div v-else-if="earlyPending && earlyCheckin.prioritised" class="rsv-early__actions">
        <button type="button" class="rsv-eb rsv-eb--ok" :disabled="earlyBusy" @click="earlyAsk('approve')">{{ $t("reservasSmallHc.reservationData.actionReadyNow") }}</button>
        <button type="button" class="rsv-eb rsv-eb--no" :disabled="earlyBusy" @click="earlyAsk('reject')">{{ $t("reservasSmallHc.reservationData.actionNotPossible") }}</button>
      </div>
    </div>

    <!--
      Ampliación de la estancia — entre la entrada y la salida, que es donde cae
      en el tiempo. Sin ella, una reserva con la estancia alargada y la salida
      tardía enseñaba solo la salida.
    -->
    <div v-if="stayExtension" class="rsv-early rsv-ext" :class="{ 'rsv-ext--ok': extDecision === 'confirmed' }">
      <span class="rsv-early__ic">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="12" y1="14" x2="12" y2="18"/><line x1="10" y1="16" x2="14" y2="16"/></svg>
      </span>
      <div class="rsv-early__txt">
        <strong>{{ extTitle }}</strong>
        <span>{{ extLine }}</span>
      </div>
      <div v-if="extDecision === 'awaiting'" class="rsv-early__wait">
        {{ $t("reservasSmallHc.reservationData.awaitingPayment") }}
      </div>
    </div>

    <!--
      Salida tardía — su propia caja y su propio color, debajo de la entrada.
      Dice a qué hora se va y cómo está el pago: pendiente (con el aviso de que
      falta que pague), confirmada (pagada, de cortesía o en efectivo) o rechazada.
    -->
    <div v-if="lateCheckout" class="rsv-early rsv-late" :class="lateBoxClass">
      <span class="rsv-early__ic">
        <svg v-if="lateDecision === 'rejected'" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
      </span>
      <div class="rsv-early__txt">
        <strong>{{ lateTitle }}</strong>
        <span>{{ lateLine }}</span>
      </div>
      <div v-if="lateDecision === 'awaiting'" class="rsv-early__wait">
        {{ $t("reservasSmallHc.reservationData.awaitingPayment") }}
      </div>
    </div>

    <!--
      Modal de confirmación de la decisión sobre el early check-in.
      El contenedor no se mueve nunca; lo que viaja a <body> al abrirse es la
      ventana (ver el watch de `earlyModal.visible`). Así Vue no pierde la cuenta
      de los hermanos de la ficha.
    -->
    <div class="rsv-em-host">
    <div v-if="earlyModal.visible" ref="earlyModal" class="rsv-em-back" @click.self="earlyCloseModal">
      <div class="rsv-em" role="dialog" aria-modal="true">
        <div class="rsv-em__icon" :class="earlyModal.action === 'reject' ? 'rsv-em__icon--no' : 'rsv-em__icon--ok'">
          <svg v-if="earlyModal.action === 'reject'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          <svg v-else-if="earlyModal.action === 'tentative'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15 14"/></svg>
          <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        </div>
        <h3 class="rsv-em__title">{{ earlyModalTitle }}</h3>
        <p class="rsv-em__text">
          {{ earlyModalText }}
          <strong>{{ reservationData && reservationData.nombre }}</strong>.
          <template v-if="earlyModal.action === 'reject'"> {{ $t("reservasSmallHc.reservationData.cannotUndo") }}</template>
        </p>
        <div v-if="earlyModal.action === 'approve-time'" class="rsv-em__field">
          <label>{{ $t("reservasSmallHc.reservationData.checkinTimeLabel") }}</label>
          <input type="time" v-model="earlyModal.time" />
          <small v-if="earlyCheckin && earlyCheckin.time" class="rsv-em__hint">
            {{ $t("reservasSmallHc.reservationData.requestedTimeHint", { time: earlyCheckin.original_time || earlyCheckin.time }) }}
          </small>
        </div>
        <div class="rsv-em__actions">
          <button type="button" class="rsv-eb rsv-eb--ghost" @click="earlyCloseModal">{{ $t("reservasSmallHc.reservationData.cancel") }}</button>
          <button
            type="button"
            class="rsv-eb"
            :class="earlyModal.action === 'reject' ? 'rsv-eb--no-solid' : 'rsv-eb--ok'"
            :disabled="earlyBusy || (earlyModal.action === 'approve-time' && !earlyModal.time)"
            @click="earlyProceed"
          >{{ earlyModalCta }}</button>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.rsv-hd {
  font-family: "DM Sans", sans-serif;
}

.rsv-hd__eyebrow {
  display: block;
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: var(--rsv-ink-4, #9aa2b5);
  font-family: "DM Sans", sans-serif;
}

.rsv-hd__role {
  display: block;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.3px;
  color: var(--rsv-ink-4, #9aa2b5);
  font-family: "DM Sans", sans-serif;
  margin-bottom: 1px;
}

/* ── Cabecera ── */
.rsv-hd__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  margin-top: 10px;
}

.rsv-hd__id {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
  flex: 1;
}

.rsv-hd__avatar {
  width: 54px;
  height: 54px;
  flex-shrink: 0;
  border-radius: 14px;
  background: linear-gradient(135deg, #5e72e4, #673de6);
  color: #fff;
  display: grid;
  place-items: center;
  font-weight: 700;
  font-size: 20px;
  letter-spacing: -0.5px;
  box-shadow: 0 6px 16px rgba(103, 61, 230, 0.25);
}

.rsv-hd__id-text {
  min-width: 0;
}

.rsv-hd__name {
  margin: 0;
  /* Escala según el espacio disponible: nombres largos entran sin romper */
  font-size: clamp(20px, 1.6vw, 25px);
  font-weight: 700;
  letter-spacing: -0.5px;
  line-height: 1.15;
  color: var(--rsv-ink, #0e1320);
}

.rsv-hd__meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
  flex-wrap: wrap;
  font-size: 12.5px;
  color: var(--rsv-ink-3, #6b7388);
  font-family: "DM Sans", sans-serif;
}

.rsv-hd__ota {
  margin-right: -1px;
}

.rsv-hd__code {
  font-weight: 600;
  color: var(--rsv-ink-3, #6b7388);
}

.rsv-hd__dot {
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: var(--rsv-ink-4, #9aa2b5);
}

/* ── Contacto compacto (email + teléfono) ── */
.rsv-hd__contact {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 6px;
  flex-wrap: wrap;
}
.rsv-hd__cti {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  font-family: "DM Sans", sans-serif;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--rsv-ink-2, #3a4156);
  text-decoration: none;
}
a.rsv-hd__cti:hover {
  color: var(--rsv-blue, #5e72e4);
}
/* Dato ausente: se lee como hueco declarado, no como un contacto real. */
.rsv-hd__cti--empty {
  color: var(--rsv-ink-4, #9aa2b5);
  font-weight: 500;
  font-style: italic;
}
.rsv-hd__cti svg {
  color: var(--rsv-ink-4, #9aa2b5);
  flex-shrink: 0;
}
.rsv-hd__cti-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 230px;
}
.rsv-hd__flag {
  width: 18px;
  height: 13px;
  object-fit: cover;
  border-radius: 2px;
  box-shadow: 0 0 0 1px rgba(16, 24, 40, 0.08);
  flex-shrink: 0;
}
.rsv-hd__wa {
  display: inline-grid;
  place-items: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #25d366;
  color: #fff;
  flex-shrink: 0;
  transition: transform 0.16s ease;
}
.rsv-hd__wa:hover {
  transform: translateY(-1px);
}

/* ── Estado (derecha) ── */
.rsv-hd__status {
  flex-shrink: 0;
}

.rsv-status-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 700;
}

.rsv-status-pill--ok {
  background: #fff;
  color: var(--rsv-green-700, #0a8a3a);
  border: 1px solid var(--rsv-green-100, #cdefd9);
  text-transform: capitalize;
}

.rsv-status-pill--cancelled {
  background: rgba(243, 61, 61, 0.12);
  color: #f12a1c;
  border: 1px solid rgba(243, 61, 61, 0.18);
}

.rsv-status-dot {
  position: relative;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--rsv-green, #11b14d);
}
.rsv-status-dot::after {
  content: "";
  position: absolute;
  inset: -3px;
  border-radius: 50%;
  background: var(--rsv-green, #11b14d);
  opacity: 0.25;
  animation: rsvPulse 1.8s ease-out infinite;
}

@keyframes rsvPulse {
  0% { transform: scale(0.8); opacity: 0.4; }
  70% { transform: scale(2); opacity: 0; }
  100% { opacity: 0; }
}

/* Dropdown de estado estilizado como pill */
:deep(.rsv-status-dd) .dropdown-toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 12px;
  border: 1px solid var(--rsv-green-100, #cdefd9);
  background: var(--rsv-green-50, #e7f7ee);
  color: var(--rsv-green-700, #0a8a3a);
  font-weight: 700;
  font-size: 13px;
  box-shadow: none;
}
:deep(.rsv-status-dd) .dropdown-toggle::after {
  display: none;
}
:deep(.rsv-status-dd) .dropdown-toggle:focus {
  box-shadow: none;
}

/* ── Tira de KPIs ── */
.rsv-hd__kpis {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0;
  margin-top: 20px;
  border: 1px solid var(--rsv-line, #ecedf2);
  border-radius: 16px;
  overflow: hidden;
  background: var(--rsv-line-2, #f3f4f8);
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04),
    0 10px 24px -14px rgba(16, 24, 40, 0.14);
}

.rsv-kpi {
  background: #fff;
  padding: 15px 16px 15px 18px;
  border-right: 1px solid var(--rsv-line, #ecedf2);
  position: relative;
  /* isolation: crea un contexto de apilamiento propio para que el z-index:1
     de las etiquetas/valores NO escape y se cuele por encima de los modales. */
  isolation: isolate;
  overflow: hidden;
  cursor: default;
  transition: background 0.24s ease;
}
.rsv-kpi:last-child {
  border-right: none;
}

/* Halo de color del tono (aparece en hover) */
.rsv-kpi::after {
  content: "";
  position: absolute;
  inset: 0;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.3s ease;
  background: radial-gradient(130% 130% at 0% 0%, rgba(17, 177, 77, 0.08), transparent 58%);
}
.rsv-kpi--blue::after {
  background: radial-gradient(130% 130% at 0% 0%, rgba(94, 114, 228, 0.08), transparent 58%);
}
.rsv-kpi--purple::after {
  background: radial-gradient(130% 130% at 0% 0%, rgba(103, 61, 230, 0.08), transparent 58%);
}
.rsv-kpi:hover::after {
  opacity: 1;
}

/* Barra de acento lateral (crece y brilla en hover) */
.rsv-kpi::before {
  content: "";
  position: absolute;
  left: 0;
  top: 14px;
  bottom: 14px;
  width: 3px;
  border-radius: 0 3px 3px 0;
  background: var(--rsv-green, #11b14d);
  transition: top 0.3s cubic-bezier(0.22, 0.8, 0.32, 1),
    bottom 0.3s cubic-bezier(0.22, 0.8, 0.32, 1), box-shadow 0.3s ease;
}
.rsv-kpi--blue::before { background: var(--rsv-blue, #5e72e4); }
.rsv-kpi--purple::before { background: var(--rsv-purple, #673de6); }
.rsv-kpi:hover::before {
  top: 0;
  bottom: 0;
}
.rsv-kpi--green:hover::before { box-shadow: 0 0 14px -1px rgba(17, 177, 77, 0.55); }
.rsv-kpi--blue:hover::before { box-shadow: 0 0 14px -1px rgba(94, 114, 228, 0.55); }
.rsv-kpi--purple:hover::before { box-shadow: 0 0 14px -1px rgba(103, 61, 230, 0.55); }

.rsv-kpi__label {
  display: block;
  position: relative;
  z-index: 1;
  margin-left: 8px;
  font-size: 10.5px;
  color: var(--rsv-ink-4, #9aa2b5);
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  font-family: "DM Sans", sans-serif;
}

.rsv-kpi__value {
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
  z-index: 1;
  margin-left: 8px;
  margin-top: 4px;
  font-size: 15px;
  font-weight: 700;
  color: var(--rsv-ink, #0e1320);
  letter-spacing: -0.3px;
}
.rsv-kpi__value--mono {
  font-family: "DM Mono", ui-monospace, monospace;
  letter-spacing: -0.2px;
}

.rsv-kpi__edit {
  width: 16px;
  height: 16px;
  stroke: var(--rsv-blue, #5e71e4);
  cursor: pointer;
  flex-shrink: 0;
}

.rsv-kpi__sub {
  display: block;
  position: relative;
  z-index: 1;
  margin-left: 8px;
  margin-top: 2px;
  font-size: 11.5px;
  color: var(--rsv-ink-3, #6b7388);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-family: "DM Sans", sans-serif;
}
.rsv-rate-chip {
  display: inline-block;
  position: relative;
  z-index: 1;
  margin-left: 8px;
  margin-top: 6px;
  font-size: 11px;
  font-weight: 700;
  color: #5e71e4;
  background: #eef0ff;
  border-radius: 999px;
  padding: 2px 9px;
  font-family: "DM Sans", sans-serif;
}

/* ── Early check-in solicitado ── */
.rsv-early {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 12px;
  padding: 12px 16px;
  border-radius: 12px;
  background: linear-gradient(135deg, #f3eeff 0%, #fbf4ff 100%);
  border: 1px solid #ddd0ff;
  font-family: "DM Sans", sans-serif;
}
/* «Esperando el pago»: un estado, no un boton. Va donde iban las acciones para
   que se lea en el mismo sitio donde el anfitrion las buscaba. */
.rsv-early__wait {
  margin-left: auto;
  align-self: center;
  font-size: 12.5px;
  font-weight: 600;
  color: #a06d12;
  background: #fdf6e6;
  border: 1px solid #f2e2bd;
  border-radius: 9px;
  padding: 7px 12px;
  white-space: nowrap;
}

.rsv-early__actions {
  margin-left: auto;
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: flex-end;
}
.rsv-eb {
  border: 1px solid transparent;
  border-radius: 9px;
  padding: 7px 13px;
  font-family: inherit;
  font-size: 12.5px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.14s;
}
.rsv-eb:disabled { opacity: 0.55; cursor: not-allowed; }
.rsv-eb--ok { background: #673de6; color: #fff; }
.rsv-eb--ok:hover:not(:disabled) { background: #5a32d0; }
.rsv-eb--alt { background: #fff; color: #4b4570; border-color: #ddd0ff; }
.rsv-eb--alt:hover:not(:disabled) { background: #f6f3ff; }
.rsv-eb--no { background: #fff; color: #be123c; border-color: #f1d2d7; }
.rsv-eb--no:hover:not(:disabled) { background: #fff5f5; }
.rsv-eb--no-solid { background: #be123c; color: #fff; }
.rsv-eb--ghost { background: #f3f4f6; color: #4b5563; }

/* Modal de decisión del early check-in */
/* El contenedor no ocupa sitio en la ficha: solo guarda el hueco de la ventana. */
.rsv-em-host { display: contents; }
/* La ventana se pinta desde <body> y tiene que quedar por encima del drawer de la
   reserva (PrimeVue lo pone en baseZIndex 11050 + su contador, ~12100). */
.rsv-em-back {
  position: fixed; inset: 0; z-index: 13000;
  background: rgba(20, 18, 33, 0.45);
  display: flex; align-items: center; justify-content: center; padding: 18px;
}
.rsv-em {
  background: #fff; border-radius: 18px; padding: 26px 24px 20px; width: 100%; max-width: 400px;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.22);
  font-family: "DM Sans", sans-serif; text-align: center;
}
.rsv-em__icon {
  width: 54px; height: 54px; border-radius: 16px; margin: 0 auto 14px;
  display: flex; align-items: center; justify-content: center;
}
.rsv-em__icon svg { width: 24px; height: 24px; }
.rsv-em__icon--ok { background: #efeafd; color: #5a32d0; }
.rsv-em__icon--no { background: #fff1f2; color: #be123c; }
.rsv-em__title { margin: 0 0 8px; font-size: 18px; font-weight: 800; color: #131720; letter-spacing: -0.01em; }
.rsv-em__text { margin: 0; font-size: 13.5px; line-height: 1.55; color: #6b7280; }
.rsv-em__text strong { color: #374151; font-weight: 700; }
.rsv-em__field { margin-top: 14px; text-align: left; }
.rsv-em__field label { display: block; font-size: 12px; font-weight: 700; color: #6b7280; margin-bottom: 5px; }
.rsv-em__field input[type="time"] {
  width: 100%; box-sizing: border-box; padding: 9px 12px; font-family: inherit; font-size: 14px;
  border: 1.5px solid #e3e0f3; border-radius: 10px; color: #374151;
}
.rsv-em__field input[type="time"]:focus { outline: none; border-color: #673de6; }
.rsv-em__hint { display: block; margin-top: 6px; font-size: 12px; color: #6b7280; }
.rsv-em__actions { display: flex; gap: 10px; margin-top: 20px; }
.rsv-em__actions .rsv-eb { flex: 1; min-height: 42px; padding: 10px; }
.rsv-early__ic {
  width: 34px;
  height: 34px;
  border-radius: 9px;
  background: #673de6;
  color: #fff;
  display: grid;
  place-items: center;
  flex-shrink: 0;
}
.rsv-early__txt { display: flex; flex-direction: column; line-height: 1.3; }
.rsv-early__txt strong { font-size: 13.5px; color: #4b2fb0; font-weight: 700; }
.rsv-early__txt span { font-size: 12.5px; color: #6b5aa8; }

/* Variante hora de llegada normal/tardía (no early): tono azul, menos alarmante */
.rsv-early--plain {
  background: linear-gradient(135deg, #eef2ff 0%, #f3f7ff 100%);
  border-color: #cdd9f7;
}
.rsv-early--plain .rsv-early__ic { background: #3f5bd6; }
.rsv-early--plain .rsv-early__txt strong { color: #2f3f9e; }
.rsv-early--plain .rsv-early__txt span { color: #5566a8; }

/* Ya decidida: verde si se aprobó, rojo apagado si se rechazó. Priorizada se
   queda en el morado de «pendiente», que es lo que sigue siendo. */
.rsv-early--ok {
  background: linear-gradient(135deg, #e8f8ee 0%, #f3fbf6 100%);
  border-color: #bde6cc;
}
.rsv-early--ok .rsv-early__ic { background: #11b14d; }
.rsv-early--ok .rsv-early__txt strong { color: #0b7a36; }
.rsv-early--ok .rsv-early__txt span { color: #3f7a57; }
.rsv-early--no {
  background: #fff6f7;
  border-color: #f3d3da;
}
.rsv-early--no .rsv-early__ic { background: #be123c; }
.rsv-early--no .rsv-early__txt strong { color: #9f1239; }
.rsv-early--no .rsv-early__txt span { color: #8a4b58; }

/* ── Salida tardía: su propio color para no confundirla con la entrada ──
   Ámbar mientras falta el pago; azul cielo cuando ya está confirmada (el verde
   es de la entrada aprobada y, una debajo de otra, se confundían). */
/* Ampliación: violeta mientras espera, verde cuando está hecha. */
.rsv-ext {
  background: linear-gradient(135deg, #f4f0ff 0%, #faf8ff 100%);
  border-color: #d9cdfb;
}
.rsv-ext .rsv-early__ic { background: #673de7; }
.rsv-ext .rsv-early__txt strong { color: #4c2bb3; }
.rsv-ext .rsv-early__txt span { color: #6b5aa6; }
.rsv-ext--ok {
  background: linear-gradient(135deg, #eaf8ef 0%, #f5fcf7 100%);
  border-color: #bfe8cd;
}
.rsv-ext--ok .rsv-early__ic { background: #16a34a; }
.rsv-ext--ok .rsv-early__txt strong { color: #14632f; }
.rsv-ext--ok .rsv-early__txt span { color: #4b7a5c; }

.rsv-late {
  background: linear-gradient(135deg, #fff6ec 0%, #fffaf3 100%);
  border-color: #fbd7ae;
}
.rsv-late .rsv-early__ic { background: #e86a10; }
.rsv-late .rsv-early__txt strong { color: #9a3d0c; }
.rsv-late .rsv-early__txt span { color: #94603a; }
.rsv-late--ok {
  background: linear-gradient(135deg, #e9f5fd 0%, #f4faff 100%);
  border-color: #b8dbf4;
}
.rsv-late--ok .rsv-early__ic { background: #0b84c6; }
.rsv-late--ok .rsv-early__txt strong { color: #0a5687; }
.rsv-late--ok .rsv-early__txt span { color: #4a7090; }
.rsv-late--no {
  background: #fff6f7;
  border-color: #f3d3da;
}
.rsv-late--no .rsv-early__ic { background: #be123c; }
.rsv-late--no .rsv-early__txt strong { color: #9f1239; }
.rsv-late--no .rsv-early__txt span { color: #8a4b58; }

.spinner-border {
  display: inline-block;
  width: 1rem;
  height: 1rem;
  vertical-align: text-bottom;
  border: 0.2em solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: spinner-border 0.75s linear infinite;
}

@keyframes spinner-border {
  to {
    transform: rotate(360deg);
  }
}

/* ── Responsive ── */
@media (max-width: 900px) {
  .rsv-hd__kpis {
    grid-template-columns: 1fr 1fr;
  }
  .rsv-kpi:nth-child(2)::after,
  .rsv-kpi:nth-child(2) {
    border-right: none;
  }
}

@media (max-width: 560px) {
  .rsv-hd__top {
    flex-direction: column;
    gap: 12px;
  }
  .rsv-hd__name {
    font-size: 23px;
  }
  .rsv-hd__kpis {
    grid-template-columns: 1fr;
  }
  .rsv-kpi {
    border-right: none;
    border-bottom: 1px solid var(--rsv-line, #ecedf2);
  }
  .rsv-kpi:last-child {
    border-bottom: none;
  }
}
</style>
